// 导入protobuf定义和事件发射器
import protoRoot from './proto.js';
import Emitter from 'tiny-emitter';

// 获取protobuf消息类型定义
// const InformMessage = protoRoot.lookup('ai.talecast.webapp.proto.InformMessage');
// const Message = protoRoot.lookup('ai.talecast.webapp.proto.Message');
// export const MessageEventType = protoRoot.lookup('ai.talecast.webapp.proto.EventType').values;
// const InformType = protoRoot.lookup('ai.talecast.webapp.proto.informType').values;

const InformMessage = protoRoot.ai.talecast.webapp.proto.InformMessage;
const Message = protoRoot.ai.talecast.webapp.proto.Message;
export const MessageEventType = protoRoot.ai.talecast.webapp.proto.WsEventTypeProto;
const InformType = protoRoot.ai.talecast.webapp.proto.informType;

// WebSocket相关常量
const HEART_BEAT_INTERVAL = 15; // 心跳间隔,单位秒
const HEART_BEAT_TIMEOUT = 10; // 心跳超时,单位秒
const RECONNECT_TIMEOUT = 2; // 重连延迟，单位秒
// const MAX_RETRY_COUNT = 3; // 最大重试次数

// MessageEmitter状态枚举
const MESSAGE_EMITTER_STATES = {
  UNINITIALIZED: 0, // 未初始化
  CONNECTED: 1, // 已连接
  RECONNECTING: 3, // 重连中
  DISCONNECTED: 4, // 已断开
  DESTROYED: 5, // 已销毁
};

// 消息发射器类,继承自Emitter
class MessageEmitter extends Emitter {
  #state = MESSAGE_EMITTER_STATES.UNINITIALIZED; // 当前状态
  #socket; // WebSocket实例
  #heartbeat; // 心跳定时器
  #retryCount = 0; // 重试次数
  #heartbeatTimeout; // 新增：心跳超时定时器

  constructor() {
    super();
  }

  // 初始化
  async init() {
    await this.#createSocket();
  }

  // 销毁实例
  destroy() {
    this.#switchState(MESSAGE_EMITTER_STATES.DESTROYED);
    this.#dispose();
  }

  // 清理资源
  #dispose() {
    this.#stopHeartbeat();
    if (this.#socket) {
      this.#socket.close();
    }
    this.#socket = null;
  }

  // 重连逻辑
  #reconnect() {
    console.error(`ws reconnect: ${new Date().toLocaleString()}`);
    this.#switchState(MESSAGE_EMITTER_STATES.RECONNECTING);
    this.#retryCount++;
    this.#dispose();
    setTimeout(() => {
      this.#createSocket().then(() => {
        this.emit('RETRY_SUCCESS', this.#retryCount);
      });
    }, 1000);
  }

  // 切换状态
  #switchState(state) {
    this.#state = state;
  }

  // 创建WebSocket连接
  async #createSocket() {
    try {
      this.#socket = await createSocket();
      this.#retryCount = 0;
      this.#switchState(MESSAGE_EMITTER_STATES.CONNECTED);
      this.#bindEvent();
      this.#startHeartbeat();
    } catch {
      setTimeout(() => {
        this.#reconnect();
      }, RECONNECT_TIMEOUT * 1000);
      throw new Error('reconnect failed');
    }
  }

  // 绑定WebSocket事件
  #bindEvent() {
    // 监听关闭事件
    this.#socket.addEventListener('close', () => {
      console.error(`ws close: ${new Date().toLocaleString()}`);
      // 如果是DESTROYED则无需重连
      if (this.#state !== MESSAGE_EMITTER_STATES.DESTROYED) {
        this.#switchState(MESSAGE_EMITTER_STATES.DISCONNECTED);
        setTimeout(() => {
          this.#reconnect();
        }, RECONNECT_TIMEOUT * 1000);
      }
    });

    // 监听消息事件
    this.#socket.addEventListener('message', async (e) => {
      // 收到任何消息都重置心跳超时计时器
      if (e.data) {
        const arrayBuffer = await e.data.arrayBuffer();
        const buffer = new Uint8Array(arrayBuffer);
        const message = Message.decode(buffer);
        console.info('ws message', MessageEventType, message);
        switch (message.eventType) {
          case MessageEventType.PONG:
            this.emit('pong');
            if (this.#heartbeatTimeout) {
              clearTimeout(this.#heartbeatTimeout);
              this.#heartbeatTimeout = null;
            }
            break;
          case MessageEventType.USER_ASSET_DETAILS:
            this.emit(MessageEventType.USER_ASSET_DETAILS, message.userAssetDetails);
            break;
          case MessageEventType.CLONE_VOICE:
            this.emit(MessageEventType.CLONE_VOICE, message.voiceMessageProto);
            break;
          case MessageEventType.USER_LEVEL_CHANGED:
            this.emit(MessageEventType.USER_LEVEL_CHANGED, message.userOrderInfoProto);
            break;
          case MessageEventType.SENTENCE_GENERATED:
            this.emit(MessageEventType.SENTENCE_GENERATED, message.sentenceGeneratedProto);
            break;
          case MessageEventType.TTS_MESSAGE:
            this.emit(MessageEventType.TTS_MESSAGE, message.ttsDetailsProto);
            break;
          case MessageEventType.EXPORT_PROCESS:
            this.emit(MessageEventType.EXPORT_PROCESS, message.exportProcessProto);
            break;
          case MessageEventType.EXPORT_SUCCESS:
            this.emit(MessageEventType.EXPORT_SUCCESS, message.exportSuccessProto);
            break;
          case MessageEventType.PROJECT_CREATED:
            this.emit(MessageEventType.PROJECT_CREATED, message.projectCreatedProto);
            break;
          case MessageEventType.SPRITE_GENERATED:
            this.emit(MessageEventType.SPRITE_GENERATED, message.spriteInfoProto);
            break;
          case MessageEventType.EXPORT_INTERRUPTED:
            this.emit(MessageEventType.EXPORT_INTERRUPTED, message.exportInterruptedProto);
            break;
          case MessageEventType.TRANSCODE_SUCCESS:
            this.emit(MessageEventType.TRANSCODE_SUCCESS, message.transcodeSuccessProto);
            break;
          case MessageEventType.PRE_DOWNLOAD_COMPLETE:
            this.emit(MessageEventType.PRE_DOWNLOAD_COMPLETE, message.preDownloadProto);
            break;
          case MessageEventType.TRANSLATE_COMPLETE:
            this.emit(MessageEventType.TRANSLATE_COMPLETE, message.translateProto);
            break;
          case MessageEventType.TTS_DETAIL:
            this.emit(MessageEventType.TTS_DETAIL, message.ttsMessageProto);
            break;
          case MessageEventType.TRANSLATION_MESSAGE:
            this.emit(MessageEventType.TRANSLATION_MESSAGE, message.translationMessageProto);
            break;
        }
      }
    });
  }

  // 开始心跳
  #startHeartbeat() {
    const message = InformMessage.create({
      type: InformType.PING,
      message: 'ping',
    });
    const buffer = InformMessage.encode(message).finish();

    const sendHeartbeat = () => {
      this.#socket.send(buffer);
      // 设置10秒超时定时器
      this.#heartbeatTimeout = setTimeout(() => {
        console.error(`Heartbeat timeout: ${new Date().toLocaleString()}`);
        this.#dispose();
      }, HEART_BEAT_TIMEOUT * 1000);
    };

    // 发送心跳
    this.#heartbeat = setInterval(sendHeartbeat, HEART_BEAT_INTERVAL * 1000);
    // 立即发送第一次心跳
    sendHeartbeat();
  }

  // 停止心跳
  #stopHeartbeat() {
    if (this.#heartbeat) {
      clearInterval(this.#heartbeat);
      this.#heartbeat = null;
    }
    if (this.#heartbeatTimeout) {
      clearTimeout(this.#heartbeatTimeout);
      this.#heartbeatTimeout = null;
    }
  }
}

// 创建WebSocket连接
export const createSocket = () => {
  const protocol = location.protocol === 'https:' ? 'wss' : 'ws';
  const socket = new WebSocket(`${protocol}://${location.host}/ws`);

  return new Promise((resolve, reject) => {
    socket.addEventListener('open', () => {
      console.info('ws open');
      resolve(socket);
    });

    socket.addEventListener('error', (e) => {
      console.info('ws error', e);
      reject(e);
    });
  });
};

// MessageEmitter单例
let messageEmitterInstance;

// 获取MessageEmitter实例
export const getMessageEmitter = async () => {
  if (!messageEmitterInstance) {
    messageEmitterInstance = new MessageEmitter();
    await messageEmitterInstance.init();
  }
  return messageEmitterInstance;
};

// 销毁MessageEmitter实例
export const destroyMessageEmitter = () => {
  messageEmitterInstance?.destroy();
  messageEmitterInstance = null;
};
